import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Loader2, Share2, Mail, Video, ShoppingBag, Check, X, FolderKanban, UserRound, ExternalLink, Wallet, RefreshCw } from "lucide-react";

function HeyGenBalanceBadge() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/video/heygen/balance", { params: refresh ? { refresh: 1 } : {} });
      setData(data);
    } catch { setData({ configured: false }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  if (!data || !data.configured) return null;
  const amount = typeof data.remaining_balance === "number" ? data.remaining_balance.toFixed(2) : "—";
  const sym = data.currency === "usd" ? "$" : (data.currency || "").toUpperCase();
  const warn = data.low_balance;
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${warn ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"}`}
      data-testid="verb-heygen-balance" title={data.auto_reload?.enabled ? `Auto-Aufladung aktiv (+${data.auto_reload.amount_usd}${sym} bei ${data.auto_reload.threshold_usd}${sym})` : "Keine Auto-Aufladung"}>
      <Wallet className="h-3 w-3" />
      <span>Guthaben: {amount}{sym}</span>
      {warn && <span className="font-semibold">· niedrig</span>}
      <button type="button" onClick={() => load(true)} disabled={loading}
        className="ml-1 text-slate-400 hover:text-white" data-testid="verb-heygen-balance-refresh" aria-label="Aktualisieren">
        <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
      </button>
    </div>
  );
}
import { useProject } from "../context/ProjectContext";

const STATUS = (ok) => ok ? (
  <span className="inline-flex items-center gap-1 text-emerald-400 text-xs" data-testid="status-ok">
    <Check className="h-3 w-3" /> verbunden
  </span>
) : (
  <span className="inline-flex items-center gap-1 text-amber-300 text-xs" data-testid="status-missing">
    <X className="h-3 w-3" /> nicht konfiguriert
  </span>
);

export default function Verbindungen() {
  const { activeProject } = useProject();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [shopForm, setShopForm] = useState({ shop_domain: "", access_token: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations").catch(() => ({ data: null }));
      setData(data);
      if (data?.shopify) {
        setShopForm({
          shop_domain: data.shopify.shop_domain || "",
          access_token: "",
        });
      } else {
        setShopForm({ shop_domain: "", access_token: "" });
      }
    } finally { setLoading(false); }
  };
  // Reload whenever active project changes — connections are per project.
  useEffect(() => { setTestResult(null); load(); }, [activeProject?.id]);

  const saveShopify = async () => {
    if (!shopForm.shop_domain.trim()) return toast.error("Shop-Domain fehlt");
    setSaving(true);
    try {
      const payload = { shop_domain: shopForm.shop_domain.trim() };
      if (shopForm.access_token) payload.access_token = shopForm.access_token;
      await api.put("/integrations/shopify", payload);
      setShopForm((f) => ({ ...f, access_token: "" }));
      toast.success("Shopify-Zugang gespeichert");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Konnte nicht speichern");
    } finally { setSaving(false); }
  };

  const testShopify = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/integrations/shopify/test");
      setTestResult(data);
      data.ok ? toast.success(data.message) : toast.error(data.message);
      load();
    } catch (err) {
      const m = err.response?.data?.detail || "Test fehlgeschlagen";
      setTestResult({ ok: false, message: m });
      toast.error(m);
    } finally { setTesting(false); }
  };

  const importShopify = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/integrations/shopify/import");
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fehlgeschlagen");
    } finally { setImporting(false); }
  };

  const disconnectShopify = async () => {
    try {
      await api.delete("/integrations/shopify");
      toast.success("Shop getrennt");
      setShopForm({ shop_domain: "", access_token: "" });
      setTestResult(null);
      load();
    } catch { toast.error("Trennen fehlgeschlagen"); }
  };

  if (loading) return <div className="p-8 text-slate-500">Lade…</div>;

  return (
    <div className="p-6 max-w-5xl space-y-6" data-testid="verbindungen-page">
      <div>
        <h1 className="text-3xl font-extrabold text-white tracking-tight">Verbindungen</h1>
        <div className="mt-2 flex items-center gap-2 text-sm text-slate-300" data-testid="verb-project-header">
          <FolderKanban className="h-4 w-4 text-emerald-400" />
          Verbindungen für:
          <span className="font-semibold text-white" data-testid="verb-project-name">
            {activeProject?.name || "—"}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-1" data-testid="verb-project-hint">
          Jedes Projekt verwaltet eigene Zugänge. Wenn du oben links das Projekt wechselst, siehst du dessen eigene Konten — Shopify, Social-Konten und alles andere sind sauber getrennt.
        </p>
      </div>

      {/* Social */}
      <Card className="bg-[#111827] border-white/5" data-testid="verb-social-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Share2 className="h-4 w-4 text-emerald-400" />
            <h2 className="font-bold text-white">Social-Konten</h2>
          </div>
          <p className="text-xs text-slate-400 mb-3">
            Verbindet pro Projekt: Meta, Instagram, TikTok, YouTube.
            Verbindung erfolgt unter Einstellungen → Plattformen.
          </p>
          <div className="flex flex-wrap gap-2">
            {(data?.social?.all_platforms || []).map((p) => {
              const ok = (data?.social?.connected_platforms || []).includes(p);
              return (
                <div key={p}
                  className={`rounded-lg border px-3 py-2 text-xs ${
                    ok ? "border-emerald-500/30 bg-emerald-500/10" : "border-white/10 bg-white/[0.02]"
                  }`}
                  data-testid={`verb-social-${p}`}>
                  <div className="font-semibold text-white capitalize">{p}</div>
                  <div className="mt-1">{STATUS(ok)}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* SMTP */}
      <Card className="bg-[#111827] border-white/5" data-testid="verb-smtp-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="h-4 w-4 text-emerald-400" />
            <h2 className="font-bold text-white">E-Mail / SMTP</h2>
            <div className="ml-auto">{STATUS(data?.smtp?.configured)}</div>
          </div>
          <p className="text-xs text-slate-400">
            {data?.smtp?.configured
              ? <>Absender: <span className="text-slate-200">{data.smtp.from}</span> (Quelle: {data.smtp.source})</>
              : "SMTP in Einstellungen hinterlegen (Host, Port, Benutzer, Passwort, Absender)."}
          </p>
        </CardContent>
      </Card>

      {/* Video-KI — Phase 19: honest per-provider status + HeyGen highlight */}
      <Card className="bg-[#111827] border-white/5" data-testid="verb-videoki-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Video className="h-4 w-4 text-emerald-400" />
            <h2 className="font-bold text-white">Video-KI</h2>
            <div className="ml-auto"><HeyGenBalanceBadge /></div>
          </div>
          <p className="text-xs text-slate-400 mb-4">
            Jeder Provider wird nur dann aktiv, wenn sein Zugang hinterlegt ist. Für Avatar-Videos
            ist zusätzlich HeyGen erforderlich (eigene Credits pro erzeugter Video-Sekunde).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(data?.video_providers || []).map((p) => {
              const isAvatar = (p.capabilities || []).includes("avatar-video");
              return (
                <div key={p.name}
                  className={`rounded-lg border p-3 ${
                    p.configured ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-white/[0.02]"
                  }`}
                  data-testid={`verb-video-${p.name}`}>
                  <div className="flex items-center gap-2">
                    {isAvatar ? <UserRound className="h-4 w-4 text-emerald-400" /> : <Video className="h-4 w-4 text-slate-400" />}
                    <div className="font-semibold text-white text-sm">{p.label}</div>
                    <div className="ml-auto">{STATUS(p.configured)}</div>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    {isAvatar ? "Avatar-Video (gesprochener Skript, HeyGen-Credits)" : "KI-Motion (bewegte Szenen)"}
                    {p.active && <span className="ml-1 text-emerald-400">· aktiv</span>}
                  </div>
                  {!p.configured && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-[11px] text-emerald-300 hover:text-emerald-200"
                        data-testid={`verb-video-${p.name}-setup-toggle`}>
                        Einrichten
                      </summary>
                      <ol className="list-decimal list-inside mt-2 space-y-1 text-[11px] text-slate-400">
                        {(p.setup_steps || []).map((s, i) => <li key={i}>{s}</li>)}
                      </ol>
                      {p.setup_url && (
                        <a href={p.setup_url} target="_blank" rel="noreferrer"
                          data-testid={`verb-video-${p.name}-setup-url`}
                          className="mt-2 inline-flex items-center gap-1 text-[11px] text-emerald-300 hover:text-emerald-200">
                          Zum Anbieter <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      <div className="mt-2 text-[10px] text-slate-500">
                        Env-Keys im Server: {(p.env_keys || []).join(", ") || "—"}
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Shopify */}
      <Card className="bg-[#111827] border-white/5" data-testid="verb-shopify-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4 text-emerald-400" />
            <h2 className="font-bold text-white">Shopify</h2>
            <div className="ml-auto">{STATUS(data?.shopify?.connected && data?.shopify?.access_token_set)}</div>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            So verbindest du: In deinem Shopify-Adminbereich → Apps → "App entwickeln" → Admin-API-Scopes für
            <code className="mx-1 text-slate-300">read_products</code> aktivieren → Access-Token kopieren und hier einfügen.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300 text-xs mb-1 block">Shop-Domain</Label>
              <Input value={shopForm.shop_domain}
                onChange={(e) => setShopForm((f) => ({ ...f, shop_domain: e.target.value }))}
                placeholder="mein-shop.myshopify.com"
                className="bg-white/5 border-white/10 text-slate-100"
                data-testid="shopify-domain" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs mb-1 block">
                Admin-API-Access-Token
                {data?.shopify?.access_token_set && <span className="text-emerald-400 text-[10px]"> · gesetzt</span>}
              </Label>
              <Input type="password" value={shopForm.access_token}
                onChange={(e) => setShopForm((f) => ({ ...f, access_token: e.target.value }))}
                placeholder={data?.shopify?.access_token_set ? "•••" : "shpat_..."}
                className="bg-white/5 border-white/10 text-slate-100"
                data-testid="shopify-token" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={saveShopify} disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
              data-testid="shopify-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Speichern
            </Button>
            <Button onClick={testShopify} disabled={testing || !data?.shopify?.access_token_set}
              variant="outline"
              className="border-emerald-500/30 bg-transparent text-emerald-300 hover:bg-emerald-500/10"
              data-testid="shopify-test">
              {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Verbindung testen
            </Button>
            <Button onClick={importShopify} disabled={importing || !data?.shopify?.access_token_set}
              variant="outline"
              className="border-white/10 bg-transparent text-slate-200 hover:bg-white/5"
              data-testid="shopify-import">
              {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Produkte importieren
            </Button>
            {data?.shopify?.connected && (
              <Button onClick={disconnectShopify} variant="ghost"
                className="text-slate-400 hover:text-rose-300"
                data-testid="shopify-disconnect">
                Trennen
              </Button>
            )}
          </div>

          {testResult && (
            <div className={`rounded-md border p-2 text-xs ${
              testResult.ok
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                : "border-rose-500/30 bg-rose-500/10 text-rose-200"
            }`} data-testid="shopify-test-result">
              {testResult.ok ? "✓" : "✗"} {testResult.message}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
